import { useCallback, useState } from "react";

// There's no modal component anywhere in this codebase yet — alert()/
// window.confirm() were standing in for one. This is a small, dependency-
// free replacement styled to match the app (same navy #10304d used for
// primary buttons and the top nav, same border-radius/shadow language as
// .auth-card elsewhere in index.css) rather than the browser's native
// unstyled popup.
export default function ConfirmDialog({ open, title, message, confirmLabel = "OK", cancelLabel, onConfirm, onCancel }) {
    if (!open) return null;

    return (
        <div
            role="presentation"
            onClick={onCancel || onConfirm}
            style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(16, 24, 32, 0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                padding: "16px",
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirm-dialog-title"
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: "#ffffff",
                    borderRadius: "8px",
                    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
                    width: "100%",
                    maxWidth: "420px",
                    padding: "24px",
                }}
            >
                {title && (
                    <h3 id="confirm-dialog-title" style={{ margin: "0 0 12px", fontSize: "17px", color: "#1a1a1a" }}>
                        {title}
                    </h3>
                )}
                <p style={{ margin: 0, fontSize: "14px", lineHeight: 1.5, color: "#333" }}>{message}</p>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "22px" }}>
                    {cancelLabel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            style={{
                                padding: "8px 16px",
                                background: "#fff",
                                color: "#10304d",
                                border: "1px solid #d0d5dd",
                                borderRadius: "4px",
                                fontSize: "14px",
                                cursor: "pointer",
                            }}
                        >
                            {cancelLabel}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onConfirm}
                        autoFocus
                        style={{
                            padding: "8px 16px",
                            background: "#10304d",
                            color: "#fff",
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "14px",
                            cursor: "pointer",
                        }}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Promise-based replacement for alert()/window.confirm() — same call
// shape (await it, get a result back) so swapping call sites over is
// mechanical, but renders the styled dialog above instead of the
// browser's native popup.
//
// Usage in a component:
//   const { dialogElement, notify, confirm } = useDialog();
//   await notify("Something happened.");             // single OK button
//   const proceed = await confirm("Are you sure?");   // Cancel/Confirm, resolves true/false
//   return <>{...}{dialogElement}</>;
export function useDialog() {
    const [state, setState] = useState(null); // { title, message, confirmLabel, cancelLabel, resolve }

    const notify = useCallback((message, { title, confirmLabel = "OK" } = {}) => {
        return new Promise((resolve) => {
            setState({ title, message, confirmLabel, cancelLabel: null, resolve });
        });
    }, []);

    const confirm = useCallback((message, { title, confirmLabel = "Yes, continue", cancelLabel = "Cancel" } = {}) => {
        return new Promise((resolve) => {
            setState({ title, message, confirmLabel, cancelLabel, resolve });
        });
    }, []);

    const handleConfirm = () => {
        state?.resolve(true);
        setState(null);
    };
    const handleCancel = () => {
        state?.resolve(false);
        setState(null);
    };

    const dialogElement = (
        <ConfirmDialog
            open={!!state}
            title={state?.title}
            message={state?.message}
            confirmLabel={state?.confirmLabel}
            cancelLabel={state?.cancelLabel}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
        />
    );

    return { dialogElement, notify, confirm };
}
